import { useCallback, useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import DealerRequiredPageShell from 'app/components/ReusableUIComponents/HighLevelComponents/DealerRequiredPageShell';
import { NotAuthorizedPage } from 'app/components/NotAuthorizedPage/Loadable';
import type { DataGridMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import { EmailsGridPage } from 'app/pages/EmailsGridPage';
import { EmailTemplateGenerationProgressOverlay } from 'app/pages/EmailsGridPage/components/EmailTemplateGenerationProgressOverlay';
import { useEmailTemplateGenerationProgressHost } from 'app/pages/EmailsGridPage/hooks/useEmailTemplateGenerationProgressHost';
import HilOpportunityTypeToggle from 'app/pages/HumanInTheLoopPage/components/HilOpportunityTypeToggle';
import HilSummaryCard from 'app/pages/HumanInTheLoopPage/components/HilSummaryCard';
import HilSegmentsAttentionGridsIsland from 'app/pages/HumanInTheLoopPage/components/HilSegmentsAttentionGridsIsland';
import HilSegmentsAttentionOverlays from 'app/pages/HumanInTheLoopPage/components/HilSegmentsAttentionOverlays';
import { useHumanInTheLoopPage } from 'app/pages/HumanInTheLoopPage/hooks/useHumanInTheLoopPage';
import { createEmptyHilSegmentAttentionActionsBridge } from 'app/pages/HumanInTheLoopPage/utils/hilSegmentAttentionActionsBridge';

export default function HumanInTheLoopReview() {
  const {
    canAccess,
    hasSelectedDealer,
    selectedDealerId,
    breadcrumbsList,
    humanInTheLoopPath,
    activeSection,
    opportunityType,
    setOpportunityType,
    selectTemplatesSection,
    selectSegmentsSection,
    sectionHeading,
    counts,
    segmentsErrorCategory,
    setSegmentsErrorCategory,
    functionalSegmentsAttentionGridType,
    systemSegmentsAttentionGridType,
    segmentsAttentionGridRefreshKey,
    refreshSegmentsAttentionGrid,
    handleFunctionalSegmentsAttentionTotalDocuments,
    handleSystemSegmentsAttentionTotalDocuments,
    handleTemplatesPendingTotalDocuments,
  } = useHumanInTheLoopPage();

  const dataGridRef = useRef<DataGridMAIQRef>(null);
  const actionsBridgeRef = useRef(createEmptyHilSegmentAttentionActionsBridge());
  const [toolbarLoading, setToolbarLoading] = useState({
    retryAllLoading: false,
    resolveActionLoading: false,
  });

  const handleToolbarLoadingChange = useCallback(
    (state: { retryAllLoading: boolean; resolveActionLoading: boolean }) => {
      setToolbarLoading(prev =>
        prev.retryAllLoading === state.retryAllLoading &&
        prev.resolveActionLoading === state.resolveActionLoading
          ? prev
          : state,
      );
    },
    [],
  );

  const segmentsAttentionDescription =
    opportunityType === 'active'
      ? 'Address LIVE segment-level issues'
      : 'Address TEST segment-level issues';

  const templateGenerationProgress = useEmailTemplateGenerationProgressHost({
    dealerId: selectedDealerId,
    enabled: Boolean(canAccess && selectedDealerId),
    onTemplateUpdate: () => {
      if (activeSection === 'segments_attention') {
        refreshSegmentsAttentionGrid();
      }
    },
    progressCardTitle: 'Generating email templates...',
  });

  const showSegmentsAttention =
    activeSection === 'segments_attention' &&
    Boolean(functionalSegmentsAttentionGridType && systemSegmentsAttentionGridType);

  if (!canAccess) {
    return <NotAuthorizedPage />;
  }

  return (
    <DealerRequiredPageShell
      hasDealer={hasSelectedDealer}
      breadcrumbsList={breadcrumbsList}
      pageTitle="Human in the loop review"
      contentVariant="centered"
    >
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Automation mode
          </Typography>
          <HilOpportunityTypeToggle
            value={opportunityType}
            onChange={setOpportunityType}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <HilSummaryCard
            title="Generated Templates Pending Approval"
            description="Review and approve generated templates"
            count={counts.templatesPending}
            selected={activeSection === 'templates_pending'}
            onClick={selectTemplatesSection}
          />
          <HilSummaryCard
            title="Segments Needing Attention"
            description={segmentsAttentionDescription}
            count={counts.segmentsAttention}
            selected={activeSection === 'segments_attention'}
            onClick={selectSegmentsSection}
          />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {sectionHeading}
        </Typography>

        {activeSection === 'templates_pending' ? (
          <EmailsGridPage
            embedded
            hideGenerateEmailTemplateDropdown
            emailTemplatesGridPreset="hilPendingApproval"
            pageHeaderTitle="Generated Templates Pending Approval"
            onTotalDocumentsChange={handleTemplatesPendingTotalDocuments}
            emailTemplateBreadcrumbOrigin={{
              label: 'HIL',
              path: humanInTheLoopPath,
            }}
          />
        ) : showSegmentsAttention ? (
          <>
            <HilSegmentsAttentionGridsIsland
              actionsBridgeRef={actionsBridgeRef}
              dataGridRef={dataGridRef}
              functionalGridType={functionalSegmentsAttentionGridType!}
              systemGridType={systemSegmentsAttentionGridType!}
              refreshKey={segmentsAttentionGridRefreshKey}
              segmentsErrorCategory={segmentsErrorCategory}
              onSegmentsErrorCategoryChange={setSegmentsErrorCategory}
              onFunctionalTotalDocuments={handleFunctionalSegmentsAttentionTotalDocuments}
              onSystemTotalDocuments={handleSystemSegmentsAttentionTotalDocuments}
              retryAllLoading={toolbarLoading.retryAllLoading}
              resolveActionLoading={toolbarLoading.resolveActionLoading}
            />
            <HilSegmentsAttentionOverlays
              dealerId={selectedDealerId}
              canAccess={Boolean(canAccess)}
              opportunityType={opportunityType}
              functionalSegmentsCount={counts.functionalSegmentsAttention}
              onRefreshGrid={refreshSegmentsAttentionGrid}
              dataGridRef={dataGridRef}
              actionsBridgeRef={actionsBridgeRef}
              onToolbarLoadingChange={handleToolbarLoadingChange}
            />
          </>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select a dealer to view segments needing attention.
          </Alert>
        )}

        <EmailTemplateGenerationProgressOverlay host={templateGenerationProgress} />
      </>
    </DealerRequiredPageShell>
  );
}
